<script>
  import { ascending, descending, index } from 'd3-array';
  import RenderedMap from './RenderedMap.svelte';
  import { getColor, NEUTRAL, TIE } from '../colors.js';
  import { formatPercent } from '../utils/format.js';
  import { intcomma, pluralize } from 'journalize';

  /**
   * @type {GeoJSON.FeatureCollection}
   */
  export let geography;

  /**
   * @type {GeoJSON.FeatureCollection}
   */
  export let roads;

  /**
   * @type {GeoJSON.FeatureCollection}
   */
  export let cities;

  /**
   * @type {import('../types.d').ReadableCandidateLookup}
   */
  export let candidates;

  /**
   * @type {import('../types.d').ReadableSubunitLookup}
   */
  export let subunits;

  export let includeParty = true;

  /** @type {string} */
  export let race = '';

  /** @type {import('../types.d').ReadableRace} */
  export let feed = ''

  $: getFillStyle = (/** @type {GeoJSON.Feature} */ feature) => {
    const code = /** @type {string} */ (feature.properties.code);
    const subunit = $subunits.get(code);

    /** @type {string} */
    let color;

    if (!subunit) {
      color = NEUTRAL;
    } else if (
      subunit.total_votes <= 0 ||
      subunit.precincts_reporting_percent < 25
    ) {
      color = NEUTRAL;
    } else {
      const results = subunit.results
        .slice()
        .sort((a, b) => descending(a[1], b[1]));
      const [leaderApId, leaderVotes] = results[0];
      const [, secondVotes] = results[1];

      if (leaderVotes === secondVotes) {
        color = TIE;
      } else {
        const candidate =
          /** @type {import('../types.d').CandidateInterface}*/ (
            $candidates.get(leaderApId)
          );
        const partyColor = getColor(candidate.party);

        color = partyColor;
      }
    }

    return color;
  };

  $: findRaceData = (/** @type {GeoJSON.Feature} */ feature) => {
    if (feature && feature.properties) {
      const subunit = $subunits.get(feature.properties.code);

      if (subunit) {
        const results = subunit.results;

        const cands = results
          .filter(([_, votes]) => votes > 0)
          .slice(0, 3)
          .map(([ap_id, votes]) => {
            const candidate =
              /** @type {import('../types.d').CandidateInterface}*/ (
                $candidates.get(ap_id)
              );
            return /** @type {import('../types.d').TooltipCandidateInterface} */ ({
              ap_id,
              name: candidate.family_name,
              party: candidate.party,
              votes,
              votePercent: formatPercent(votes, subunit.total_votes),
            });
          });

        return {
          name: subunit.name,
          candidates: cands,
          totalVotes: subunit.total_votes,
          precinctsTotal: subunit.precincts_total,
          precinctsReporting: subunit.precincts_reporting,
          eevp: subunit.precincts_reporting_percent,
          includeParty,
        };
      }
    } else {
      return undefined;
    }
  };

  function generateFirstSentence(id) {
    if (id === 'CA-P0-2024-11-05') { // ca - president
      return 'Map of California’s results in the presidential race, by county.'
    } else if (id == 'CA-S8619-2024-11-05') { // schiff vs garvey
      return `Map of results in the California senate race between Democrat Adam Schiff and Republican Steve Garvey race, by county.`
    }
  }

  const candidateRanking = $feed.candidates.map((d) => d.ap_id);
  const candidateLookup = index($feed.candidates, (d) => d.ap_id);
  
  function getLeadingCount(race) {

    let noResults = false;
    let tie = false;
    let candidateIds = {}
    let tieCount = 0;
    let noResultsCount  = 0;

    for (let idx = 0; idx < race.subunits.length; idx++) {
      const subunit = race.subunits[idx];

      // first check if there are votes at all, or not enough in
      if (
        subunit.total_votes <= 0 ||
        subunit.precincts_reporting_percent < 25
      ) {
        noResults = true;
        noResultsCount++ 
        continue;
      }
      const results = subunit.results
        .slice()
        .sort((a, b) => descending(a[1], b[1]));
      const [leaderApId, leaderVotes] = results[0];
      const [, secondVotes] = results[1];

      // next check if it's a tie
      if (leaderVotes === secondVotes) {
        tie = true;
        tieCount++
        continue;
      }

      if (!candidateIds[leaderApId]) {
        candidateIds[leaderApId] = 0;
      }

      candidateIds[leaderApId]++;
    }

    // Get candidates as a list
    let candidateList = Object.entries(candidateIds)

    // Sort the list
    let sortedCandidateList = candidateList.sort((cand1, cand2) => {
      return cand2[1] - cand1[1]
    })

    // build the json that I need
    let countyCountData = {
      'candidates': sortedCandidateList,
      'tie': tieCount,
      'noLeader': noResultsCount
    }

    return countyCountData;
  }

  $: countyCountResults = getLeadingCount($feed);
  $: console.log(countyCountResults);

  function generateTiedText(x){
    if (x > 0) {
      return `There are ties between the candidates in ${x} ${x > 1 ? 'counties' : 'county'}.` 
    } else {
      return '';
    }
  }

  function generateNoLeaderText(x){
    if (x > 0) {
      return `A leader has not yet been called in ${x} ${x > 1 ? 'counties' : 'county'}.` 
    } else {
      return '';
    }
  }

  function generateLeaderText(resultsJson, totalCounties) {
    // find out how many candidates are appearing
    let candidateCount  = resultsJson.candidates.length;

    let leader1={};
    let leader2={};
    let leader3={};

    if ((candidateCount !== 0) && (resultsJson.candidates[0])){
      leader1.name = candidateLookup.get(resultsJson.candidates[0][0])?.family_name;
      leader1.count = resultsJson.candidates[0][1];
    }
    if ((candidateCount !== 0) && (resultsJson.candidates[1])){
      leader2.name = candidateLookup.get(resultsJson.candidates[1][0])?.family_name;
      leader2.count = resultsJson.candidates[1][1];
    }
    // if third candidate
    if ((candidateCount !== 0) && (resultsJson.candidates[2])){
      leader3.name = candidateLookup.get(resultsJson.candidates[2][0])?.family_name;
      leader3.count = resultsJson.candidates[2][1];
    }

    if (resultsJson.noLeader == totalCounties) { // no results at all on map
      return `A leader has not yet been called in any county.`
    } else if (candidateCount === 1){ // if only one leader atm
      return `${leader1.name} is leading in ${leader1.count} counties.`
    } else if (candidateCount === 2){ // if two leaders atm
      return `${leader1.name} is leading in ${leader1.count} counties. ${leader2.name} is leading in ${leader2.count} counties.`
    } else if (candidateCount === 3){ // if three leaders atm
      return `${leader1.name} is leading in ${leader1.count} counties. ${leader2.name} is leading in ${leader2.count} counties. ${leader3.name} is leading in ${leader3.count} counties.`
    } else if ((candidateCount === 2) && (leader1.count == leader2.count) && (leader1.count > 1)) {
      return `${leader1.name} and ${leader2.name} are both leading in ${leader2.count} counties each.`
    } else {
      return ''
    }
    
  }

  $: ariaLabel = `${generateFirstSentence($feed.id)} ${generateLeaderText(countyCountResults)} ${generateTiedText(countyCountResults.tie)} ${generateNoLeaderText(countyCountResults.noLeader)}`
</script>

<RenderedMap {getFillStyle} {findRaceData} {geography} {roads} {cities} {race} {ariaLabel}/>
